// libs
import styled, { css } from 'styled-components'
// components
import { Button } from 'components/UI'
// themes
import colors from 'themes/colors'
// icons
import UploadIconOrigin from '@mui/icons-material/Upload'
// utils
import { size } from 'polished'

const TRANSITION = '0.15s'

type CompactProps = { $compact?: boolean }

export const Anchor = styled.div`
  min-width: 0;
  position: relative;
`

const container = css<CompactProps>`
  align-items: center;
  background-color: ${colors.white100};
  box-sizing: border-box;
  display: flex;
  height: ${({ $compact }) => ($compact ? '32px' : '64px')};
  outline: none;
  transition: all ${TRANSITION};

  &:focus-visible {
    box-shadow: 0 0 0 3px ${colors.blue100};
  }
`

// empty form field: the dropzone view the Strategy field used before the redesign
export const Dropzone = styled.div<{ $isDragActive: boolean; $isActive?: boolean; $disabled?: boolean }>`
  align-items: center;
  background-color: ${colors.white100};
  border: 1px
    ${({ $isDragActive, $isActive }) =>
      $isDragActive || $isActive ? `solid ${colors.blue500}` : `dashed ${colors.gray320}`};
  border-radius: 6px;
  display: flex;
  justify-content: center;
  min-height: 60px;
  outline: none;
  padding: 8px 16px;
  text-align: center;
  transition: background-color ${TRANSITION};

  ${({ $isDragActive }) =>
    $isDragActive &&
    css`
      background-color: ${colors.blue100};
    `};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `};
`

export const DropzoneContent = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`

export const DropzoneText = styled.div`
  color: ${colors.gray450};
  font-size: 16px;
`

export const ActionLink = styled.span`
  color: ${colors.blue500};
  cursor: pointer;
`

export const DropzoneIcon = styled(UploadIconOrigin)`
  ${size('28px')};

  path {
    fill: ${colors.blue500};
  }
`

// empty table cell: looks like the neighbouring underline inputs
export const Zone = styled.div<CompactProps & { $isDragActive?: boolean; $isActive?: boolean; $disabled?: boolean }>`
  ${container};
  background-color: transparent;
  border-bottom: 1px solid
    ${({ $isDragActive, $isActive }) => ($isDragActive || $isActive ? colors.blue500 : colors.gray320)};
  gap: 2px;
  height: 32px;

  ${({ $isDragActive }) =>
    $isDragActive &&
    css`
      background-color: ${colors.blue100};
    `};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `};
`

export const Item = styled.div<CompactProps & { $isDragActive?: boolean; $hideUnderline?: boolean }>`
  ${container};
  border: 1px solid ${colors.gray210};
  border-radius: 8px;
  gap: 12px;
  padding: 0 10px 0 14px;

  // in a table cell the filled value looks like the neighbouring underline inputs
  ${({ $compact }) =>
    $compact &&
    css`
      background-color: transparent;
      border-color: ${colors.gray320};
      border-style: solid;
      border-width: 0 0 1px;
      border-radius: 0;
      gap: 2px;
      padding: 0;
    `};

  ${({ $hideUnderline }) =>
    $hideUnderline &&
    css`
      border-bottom-width: 0;
    `};

  ${({ $isDragActive }) =>
    $isDragActive &&
    css`
      background-color: ${colors.blue100};
      border-color: ${colors.blue500};
    `};

  // × appears on hover, only on devices with a real pointer (touch keeps it visible)
  // RemoveButton is declared below, so it is referenced lazily to avoid the TDZ error
  @media (hover: hover) and (pointer: fine) {
    ${() => RemoveButton} {
      display: none;
    }

    &:hover ${() => RemoveButton}, &:focus-within ${() => RemoveButton} {
      display: inline-block;
    }
  }
`

// in a table cell the icon has no tile background, so the row reads like a plain input
export const Tile = styled.span<CompactProps>`
  align-items: center;
  background-color: ${({ $compact }) => ($compact ? 'transparent' : colors.blue100)};
  border-radius: 6px;
  color: ${colors.blue500};
  display: flex;
  flex: none;
  justify-content: center;
  transition: all ${TRANSITION};
  ${({ $compact }) => size($compact ? '22px' : '36px')};

  svg {
    ${({ $compact }) => size($compact ? '14px' : '18px')};
  }
`

export const Texts = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`

const ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const Title = styled.span`
  ${ellipsis};
  color: ${colors.gray450};
  font-size: 14px;
`

export const Name = styled.a<{ $isLink?: boolean }>`
  ${ellipsis};
  color: ${({ $isLink }) => ($isLink ? colors.blue500 : colors.black700)};
  display: block;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    color: ${({ $isLink }) => ($isLink ? colors.blue800 : colors.black700)};
    text-decoration: underline;
  }
`

export const PendingName = styled.span`
  ${ellipsis};
  color: ${colors.black700};
  font-size: 14px;
`

export const Hint = styled.span`
  ${ellipsis};
  color: ${colors.gray320};
  font-size: 12px;
`

export const Progress = styled.div<CompactProps & { $value: number }>`
  background-color: ${colors.gray270};
  border-radius: 2px;
  flex: none;
  height: 3px;
  margin-top: ${({ $compact }) => ($compact ? '2px' : '4px')};
  overflow: hidden;

  &::after {
    background-color: ${colors.blue500};
    content: '';
    display: block;
    height: 100%;
    transition: width ${TRANSITION};
    width: ${({ $value }) => $value}%;
  }
`

export const RemoveButton = styled(Button)<CompactProps>`
  && {
    flex: none;

    button {
      background-color: transparent !important;
      border: none;
      border-radius: 4px;
      box-shadow: none;
      color: ${colors.gray320};
      min-width: 0;
      padding: 0;
      transition: all ${TRANSITION};
      ${({ $compact }) => size($compact ? '24px' : '32px')};

      &:hover {
        color: ${colors.red300};
      }

      svg {
        ${({ $compact }) => size($compact ? '14px' : '16px')};
      }
    }
  }
`
